import type { ComponentType } from "react";
import { DitherShader } from "@/components/landing/dither-shader";
import "@/app/routes/_home/fonts.css";

interface LeftPanelFeature {
  icon: ComponentType<{ className?: string }>;
  label: string;
}

interface LeftPanelProps {
  headline: string;
  subtitle: string;
  badge: string;
  features?: LeftPanelFeature[];
  logoSrc?: string;
  productName?: string;
}

/**
 * Panel de marca de las pantallas de acceso (desktop). Mismo lenguaje visual
 * que el inicio de la landing: fondo blanco con la franja azul de píxeles
 * («arc») abajo, títulos en Schibsted Grotesk y etiquetas en mono. Sin
 * `headline`, usa el de la landing.
 */
export function LeftPanel({
  headline,
  subtitle,
  badge,
  features,
  logoSrc = "/heskala-logo.png",
  productName = "heskala",
}: LeftPanelProps) {
  return (
    <div className="home-fonts hidden lg:flex w-full min-h-screen relative overflow-hidden bg-white items-center py-12">
      <DitherShader
        variant="arc"
        className="absolute inset-x-0 -bottom-[140px] h-[380px] xl:-bottom-[160px] xl:h-[440px]"
      />

      {/* Espejo de AuthRightPanel (mitad derecha, pr-12/xl:pr-16, max-w-md
          centrado): el texto queda centrado en su mitad y a la altura de la
          tarjeta. */}
      <div className="relative z-20 flex w-1/2 justify-center pl-12 xl:pl-16">
        <div className="flex w-full max-w-md flex-col gap-8">
          <div className="flex items-center gap-2.5">
            <img
              src={logoSrc}
              alt=""
              aria-hidden="true"
              width={34}
              height={34}
              className="size-[34px]"
            />
            <span className="font-heading text-[28px] leading-none text-[#001434]">
              {productName}
            </span>
          </div>

          <div className="flex flex-col">
            {badge && (
              <p className="mb-5 font-mono text-[13px] uppercase tracking-[0.04em] text-[#002C6E]">
                {badge}
              </p>
            )}

            <h1 className="font-heading text-[40px] leading-[1.08] text-[#001434] xl:text-[48px]">
              {headline || (
                <>
                  Construye cualquier sistema.
                  <br />
                  <span className="text-[#002C6E]">Controla cada paso.</span>
                </>
              )}
            </h1>

            <p className="mt-5 max-w-[440px] text-[16px] leading-[1.7] text-[#636363]">
              {subtitle}
            </p>

            {features && features.length > 0 && (
              <ul className="mt-9 flex flex-wrap gap-2.5">
                {features.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="flex items-center gap-2 rounded-[3px] border border-[#C9D6EA] bg-white px-3 py-1.5 font-mono text-[13px] text-[#001434]"
                  >
                    <Icon className="size-3.5 text-[#002C6E]" />
                    {label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export type { LeftPanelFeature };
